/* eslint-disable eqeqeq */
import * as Sentry from "@sentry/vue";
import { apiV1 } from "@/store";

export const state = {
  agents: [],
  isAdmin: false,
  leads: [],
  me: null,
  phoneRentals: [],
  roles: [],
  users: []
};

export const getters = {
  agents: (state) => {
    return state.agents.sort(function(a, b) {
      if (a.last_name < b.last_name) {
        return -1;
      }
      if (a.last_name > b.last_name) {
        return 1;
      }
      return 0;
    });
  },
  isAdmin: state => state.isAdmin,
  hasDocumentAccess: state => state.hasDocumentAccess
};

export const actions = {
  getUserInfo: ({ commit }) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`/user`)
        .then(async resp => {
          await commit("setUserInfo", resp.data);

          if (resp && resp.data && resp.data.user) {
            Sentry.configureScope(function(scope) {
              scope.setExtra("cc_user", resp.data.user);
            });
          }

          commit("setRoles", resp.data.user.roles);
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  clearUser: ({ commit }) => {
    commit("clearUserInfo");
    return Promise.resolve(true);
  },

  validateUserRegistrationToken: ({}, token) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`/user/preregister/${token}`)
        .then((resp) => {
          resolve(resp.data);
        })
        .catch((err) => {
          reject(err.response);
        });
    });
  },

  getAllUsers: ({ commit, dispatch }, toggleStatus) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`/users?include_disabled=${toggleStatus}`)
        .then(async resp => {
          commit("setUsers", resp.data.users);
          resolve(resp);
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  },

  createUser: ({ dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.post(`/users`, payload)
        .then((resp) => {
          dispatch("handleSuccess", "User account successfully created!");
          resolve(resp);
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  },

  registerUser: ({ dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.post(`/user/register`, payload)
        .then((resp) => {
          dispatch("handleSuccess", "Account created successfully, please login using your email and password.");
          resolve(resp);
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  },

  forgotPassword: ({ commit, dispatch }, userId) => {
    return new Promise((resolve, reject) => {
      apiV1.post(`/users/${userId}/forgot-password`)
        .then((resp) => {
          dispatch("handleSuccess", "Forgot password email sent to user.");
          resolve(resp);
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  },

  resetPassword: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.post(`/user/reset-password/${payload.token}`, payload)
        .then((resp) => {
          dispatch("handleSuccess", "Your password has been reset successfully, please login using your new password.");
          resolve(resp);
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  },

  getUserProfile: ({}, id) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`/users/${id}`)
        .then(async resp => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  updateUserProfile: ({ dispatch }, user) => {
    return new Promise((resolve, reject) => {
      apiV1.patch(`/users/${user.id}`, user)
        .then((resp) => {
          dispatch("handleSuccess", "User account successfully updated!");
          resolve(resp);
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  },

  toggleUserProfile: ({ dispatch }, user) => {
    let switchStatus = user.enabled === 1 ? 0 : 1;
    user.enabled = switchStatus;
    return new Promise((resolve, reject) => {
      apiV1.patch(`/users/${user.id}`, user)
        .then((resp) => {
          dispatch("handleSuccess", "User status successfully updated!");
          resolve(resp);
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  },

  getCallCenterRoles: ({ dispatch }) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`/roles`)
        .then(async resp => {
          resolve(resp);
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  },

  getCurrentAgentLeads: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`/user/reservations`, {
        params: params
      })
        .then(async resp => {
          await commit("setCurrentAgentLeads", resp.data);
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getCurrentAgentPhoneRentals: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`/user/phone-rentals`, {
        params: params
      })
        .then(async resp => {
          await commit("setCurrentAgentPhoneRentals", resp.data);
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  getAgentsList: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`/users/agents`)
        .then(async resp => {
          await commit("setAgentsList", resp.data);
          resolve(resp);
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  },
  chuckMe: ({ dispatch }) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`/chucked`)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  }
};

export const mutations = {
  setUserInfo: (state, response) => {
    state.me = response.user;

    const adminRoles = ["super-admin", "call-center-admin", "call-center-supervisor", "call-center-manager"];
    const documentRoles = [...adminRoles, "call-center-document-manager"];
    state.isAdmin = state.me.roles.some(userRole => adminRoles.includes(userRole));
    state.hasDocumentAccess = state.me.roles.some(userRole => documentRoles.includes(userRole));
    return Promise.resolve(true);
  },
  setCurrentAgentLeads: (state, data) => {
    state.leads = data.reservations;
  },
  setCurrentAgentPhoneRentals: (state, data) => {
    state.phoneRentals = data.phone_rentals;
  },
  setAgentsList: (state, data) => {
    state.agents = data.agents;
  },
  setRoles: (state, roles) => {
    state.roles = roles;
  },
  setUsers: (state, users) => {
    state.users = users;
  },
  clearUserInfo: state => {
    state.me = null;
    state.isAdmin = false;
  },
  updateEditUser: (state, change) => {
    state.editUser[change.property] = change.value;
  }
};

export default {
  name: "user",
  state,
  getters,
  actions,
  mutations
};
